import type { MaybeRefOrGetter } from 'vue'
import type { HeroContent } from '@nimbus/types'
import type { Game } from '@game/types'
import { computed, ref, toValue, useGame, useGameOpeningHours, useHeroConfig, useTracking, useDevice, useTimeoutFn, whenever } from '#imports'

export function useHeroGame(content: MaybeRefOrGetter<HeroContent | undefined>) {
    const contentRef = computed(() => toValue(content))
    const { getGameBySlug, start } = useGame()
    const { cta } = useHeroConfig()
    const { trackEvent } = useTracking()
    const { isMobile } = useDevice()

    const { data: fetchedGame, refresh } = getGameBySlug(
        () => contentRef.value?.game ? '' : (contentRef.value?.game_slug ?? '')
    )

    const game = computed(() => contentRef.value?.game ?? fetchedGame.value)

    whenever(() => !contentRef.value?.game && contentRef.value?.game_slug, async (gameSlug) => {
        if (!gameSlug || !fetchedGame.value || fetchedGame.value.slug === gameSlug) return
        await refresh()
    }, {
        immediate: true
    })

    const { isPlayable, isClosed } = useGameOpeningHours(game)

    const gameLoading = ref(false)

    // SSR-safe timeout: auto-cancels on scope dispose so the redirect-window
    // fallback doesn't leak across Nuxt SSR requests.
    const { start: startLoadingTimeout } = useTimeoutFn(() => {
        gameLoading.value = false
    }, 5000, { immediate: false })

    async function startGame(onGameClick?: (g: Game) => void) {
        if (import.meta.server) return
        if (gameLoading.value || !game.value || !isPlayable.value) return
        gameLoading.value = true

        const useTimedLoading = isMobile

        if (useTimedLoading) {
            startLoadingTimeout()
        }

        trackEvent({
            event_type: 'Click',
            event_name: 'hero_cta_button',
            meta_data: [],
        })

        onGameClick?.(game.value)

        await start({ gameSlug: game.value.slug, providerSlug: game.value.provider_slug })

        if (!useTimedLoading) {
            gameLoading.value = false
        }
    }

    return { game, isPlayable, isClosed, gameLoading, startGame, cta, isMobile }
}
